<template>
  <div class="search-result">
    <div class="max-lg:pt-5 pb-2.5" v-if="searchedText.length < 3 && !searchInCity && filteredCities?.length">
      <span class="result-list-title">
        {{ t("index.popularDestinations") }}
      </span>
      <template v-for="(city, index) in filteredCities"">
        <SearchResultItem
          :is-top-city="true"
          :data="city.city"
        />
        <hr
          v-show="index + 1 !== filteredCities?.length"
          class="text-gray-5"
        />
      </template>
    </div>

    <template v-else>

      <div v-if="loading" class="pb-5">
        <LazyBaseSpinner
          size="lg"
          color="secondary"
          class="!block mx-auto my-10"
        />
      </div>

      <div v-else-if="searchedText.length > 2 && !searchResult?.length">
        <SearchResultNoData/>
      </div>

      <div class="max-lg:pt-5" v-else-if="searchResult && searchResult.length">
        <span class="result-list-title">
          {{ t("searchBox.searchResults") }}
        </span>
        <div class="w-full" :class="resultListClass">
          <template v-for="(item, index) in filteredSearchResult">
            <SearchResultItem
              :data="item" 
              :search-in-city="searchInCity" 
            />
            <hr
              v-show="index + 1 !== filteredSearchResult?.length"
              class="text-gray-5"
            />
          </template>
        </div>
        <NuxtLink class="see-all" :to="localePath(`/search/${searchedText}`)">
          <div class="flex justify-start items-center">
            <icon-search-bold class="search-icon" />
            <span>
              {{ `${t("searchBox.seeAllResults")} “${searchedText}”` }}
            </span>
          </div>
          <icon-arrow-forward class="text-[20px] text-primary-1 !mb-0 rtl:rotate-180" />
        </NuxtLink>
      </div>
      <div v-else></div>
    </template>
  </div>
</template>

<script setup lang="ts">
import SearchResultNoData from "./SearchBoxTwoResultNoData.vue";
import SearchResultItem from "./SearchBoxTwoResultItem.vue";
import type { ITopCities, ISearchResult } from "../../types/general";
import { computed, type PropType } from "vue";

const { t } = useI18n();
const localePath = useLocalePath();

const props = defineProps({
  searchedText: { type: String, required: true },
  loading: { type: Boolean, default: false, required: true },
  cities: {
    type: Array as PropType<Array<ITopCities> | null>,
    default: null,
    required: true,
  },
  searchResult: {
    type: Array as PropType<Array<ISearchResult> | null>,
    default: null,
    required: true,
  },
  searchInCity: { type: Boolean, default: false },
  isMobileOrTablet: { type: Boolean, default: false },
  resultListClass: { type: String, default: '' },
  isSupplierSearch: { type: Boolean, default: false }
});

const filteredCities = computed(() => {
  return props.cities && props.cities?.length > 5
    ? props.cities?.slice(0, 5)
    : props.cities;
});

const filteredSearchResult = computed(() => {
  if(props.searchInCity) {
    return props.searchResult
  } else {
    const max = props.isMobileOrTablet ? 4 : 7
    const maxProducts = props.isMobileOrTablet ? 2 : 5

    const cities =
      props.searchResult?.filter((item) => item.type === "city") || [];
    const products =
      props.searchResult?.filter((item) => item.type === "product") || [];
    // If it's supplier searchbar component
    if(props.isSupplierSearch) {
      return products.slice(0, max);
    }
    // If only cities exist
    if (cities.length > 0 && products.length === 0) {
      return cities.slice(0, max);
    }
    // If only products exist
    if (products.length > 0 && cities.length === 0) {
      return products.slice(0, max);
    }

    let selectedCities = cities.slice(0, 2);
    let selectedProducts = products.slice(0, maxProducts);
    const remainingSlots = max - (selectedCities.length + selectedProducts.length);
    if (remainingSlots > 0) {
      if (selectedCities.length < 2) {
        selectedProducts = products.slice(0, maxProducts + remainingSlots);
      } else {
        selectedCities = cities.slice(0, 2 + remainingSlots);
      }
    }

    return [...selectedCities, ...selectedProducts];
  }
});
</script>

<style scoped>
.result-list-title {
  @apply px-5 text-[11px] font-normal text-gray-1 lg:mb-2 block cursor-context-menu text-start;
}
.search-result .see-all {
  @apply bg-white flex justify-between items-center px-3 py-4 rounded-b-lg max-lg:!shadow-none;
  box-shadow: 0px -4px 10px 0px rgba(0, 0, 0, 0.05);
}
.search-result .see-all span {
  @apply block ps-3 text-primary-1 text-heading-6;
}
.search-result .see-all .search-icon {
  @apply text-[36px] text-gray-1 p-[7px] bg-gray-5 !mb-0 rounded-full lg:hidden
}
</style>
